import { useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useChatTokenQuery } from "Api/Queries/Chat/useChatTokenQuery";
import { useDisclosedNamesMapQuery } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { isAuthenticated } from "Utils/AuthUtils";
import { ChatLayout } from "Components/Chat/ChatLayout";
import { ChatContent } from "Components/Chat/ChatContent";
import { ChatDisclosedNamesAlert } from "Components/Chat/ChatDisclosedNamesAlert";
import { ChatOnboardingGate } from "Components/Chat/ChatOnboardingGate";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat;

export const ChatPage = () => {
  const [isChatDetailView, setIsChatDetailView] = useState(false);
  const { t } = useResource();
  const user = useLoggedInUser();
  const isUserAuthenticated = isAuthenticated(user);
  const {
    data: tokenData,
    isLoading,
    isError,
  } = useChatTokenQuery(isUserAuthenticated, user?.activeContextPublicID);

  // Chat identities are IdentityUser-keyed — non-migrated users cannot chat yet.
  const isChatUnavailable = user?.activeContextPublicID == null || isError;

  // Prefetch only: the disclosed-names map (ADR-0007) is consumed inside ChatContent and by
  // ChatDisclosedNamesAlert, but kicking it off here lets it load alongside the chat token
  // instead of after it.
  useDisclosedNamesMapQuery();

  if (isChatUnavailable) {
    return (
      <ChatLayout isChatDetailView={false}>
        <Stack height="100%" alignItems="center" justifyContent="center" p={4}>
          <Typography color="text.secondary" textAlign="center">
            {t(RESOURCES.Unavailable)}
          </Typography>
        </Stack>
      </ChatLayout>
    );
  }

  return (
    <ChatLayout isChatDetailView={isChatDetailView}>
      <ChatOnboardingGate>
        {isLoading || !tokenData ? (
          <CenteredCircularProgress />
        ) : (
          <>
            <ChatDisclosedNamesAlert />
            <ChatContent
              chatTokenData={tokenData}
              onDetailViewChange={setIsChatDetailView}
            />
          </>
        )}
      </ChatOnboardingGate>
    </ChatLayout>
  );
};
